import React, { useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import Navbar from './Navbar';
import SiteFooter from './SiteFooter';
import PressKit from './PressKit';
import { getGame } from './games';
import { pressFolder, shortDescription, storyIntroduction, closing, media, features } from './othercrownContent';
import './Othercrown.css';
import { attachOthercrownMedia } from './othercrownMedia';

const GAME = getGame('othercrown');

function Metadata({ press = false }) {
  const title = press ? 'Othercrown — Press Kit' : 'Othercrown — Voidmaw';
  return <Helmet>
    <link rel="preconnect" href="https://fonts.googleapis.com" />
    <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
    <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=IM+Fell+English&family=Spectral:ital,wght@0,400;1,400&display=swap" />
    <title>{title}</title>
    <meta name="description" content={shortDescription} />
    <link rel="canonical" href={`https://www.voidmaw.com/othercrown${press ? '/press-kit' : ''}`} />
    <meta property="og:title" content={title} />
    <meta property="og:description" content={shortDescription} />
    <meta property="og:url" content={`https://www.voidmaw.com/othercrown${press ? '/press-kit' : ''}`} />
    <meta property="og:image" content={`https://www.voidmaw.com${media.keyArt}`} />
    <meta name="twitter:title" content={title} />
    <meta name="twitter:description" content={shortDescription} />
    <meta name="twitter:image" content={`https://www.voidmaw.com${media.keyArt}`} />
    <meta property="twitter:url" content={`https://www.voidmaw.com/othercrown${press ? '/press-kit' : ''}`} />
  </Helmet>;
}

// Pixel embers around the wishlist button: start across its width (%), drift (px), size (px), duration and delay (ms).
const EMBERS = [
  [4, -70, -84, 6, 2600, 0], [10, -34, 58, 4, 2200, 700], [18, -44, 50, 6, 2800, 1500], [26, -14, 66, 4, 2400, 300],
  [34, -24, 44, 6, 3000, 1900], [42, 12, 62, 4, 2300, 1100], [50, -8, 52, 6, 2700, 200], [58, 20, 68, 4, 2500, 1700],
  [66, 14, 46, 6, 2900, 900], [74, 34, 60, 4, 2300, 2100], [82, 26, 52, 6, 2600, 500], [90, 50, 56, 4, 2400, 1300],
  [97, 66, -78, 6, 2700, 800], [1, -52, -52, 4, 2300, 1800], [99, 46, -56, 4, 2500, 100], [7, -84, -36, 5, 3100, 1200],
  [94, 86, -40, 5, 3000, 2300], [2, -38, -108, 5, 3200, 2500], [20, -18, -124, 5, 3000, 600], [36, 10, -132, 4, 3300, 2000],
  [52, -12, -126, 6, 3100, 1400], [68, 16, -134, 4, 2900, 2600], [84, 22, -122, 5, 3200, 400], [98, 40, -110, 5, 3000, 1600],
];

function Clip({ index }) {
  const clip = media.clips[index];
  if (!clip) return null;
  return <figure className="oc-media" data-oc-clip="">
    <video loop muted playsInline preload="none" data-src={clip.src} poster={clip.poster} width="1920" height="1080" tabIndex="0" role="button" aria-label={`Play ${clip.label}`} data-label={clip.label}>
  </video>
    <figcaption className="oc-media-status" role="status" aria-live="polite" />
  </figure>;
}

export default function Othercrown() {
  const page = useRef(null);
  useEffect(() => attachOthercrownMedia(page.current), []);
  return <div className="oc-page" ref={page}>
    <Metadata />
    <Navbar pressHref={GAME.pressHref} steamHref={GAME.steamUrl} highlightPress />
    <main>
      <header className="oc-hero oc-hero-art">
        <img className="oc-hero-artwork" src={media.artwork} srcSet="/Othercrown/hero-960.webp 960w, /Othercrown/hero-1600.webp 1600w, /Othercrown/hero.webp 2400w" sizes="100vw" alt="" width="2400" height="1350" fetchpriority="high" />
        <div className="oc-hero-content">
          <h1>{media.logo ? <img src={media.logo} alt="Othercrown" width="1280" height="410" /> : 'Othercrown'}</h1>
          <p className="oc-genre">An auto-battler roguelite</p>
          <div className="oc-actions oc-hero-actions"><div className="oc-wishlist-wrap"><div className="oc-embers" aria-hidden="true">{EMBERS.map(([x, dx, dy, size, time, delay]) => <i key={`${x}-${delay}`} style={{ '--x': `${x}%`, '--dx': `${dx}px`, '--dy': `${dy}px`, '--size': `${size}px`, '--time': `${time}ms`, '--delay': `-${delay}ms` }} />)}</div><a className="oc-button oc-wishlist" href={GAME.steamUrl} target="_blank" rel="noopener noreferrer">Wishlist Now <span aria-hidden="true">↗</span></a></div><a className="oc-button oc-button-outline" href="#about">About the game <span aria-hidden="true">↓</span></a></div>
          <div className="oc-status">In development<br /><span className="oc-release-window">Planned release: {GAME.releaseWindow}</span></div>
        </div>
      </header>
      <section className="oc-intro oc-width" id="about" aria-labelledby="oc-about">
        <h2 id="oc-about">What is Othercrown?</h2>
        <p className="oc-lead">{shortDescription}</p>
        <Clip index={0} />
        <div className="oc-story"><p>{storyIntroduction}</p></div>
        <Clip index={1} />
      </section>
      <div className="oc-chapters oc-width">
        {features.map((feature, index) => <section className="oc-chapter" id={feature.id} key={feature.id} aria-labelledby={`title-${feature.id}`}>
          <div className="oc-chapter-heading"><h2 id={`title-${feature.id}`}>{feature.title}</h2></div>
          <p>{feature.text}</p>
          {index < 3 && <Clip index={index + 2} />}
        </section>)}
      </div>
      <section className="oc-ending oc-width"><p>{closing}</p><a className="oc-text-link" href={GAME.steamUrl} target="_blank" rel="noopener noreferrer">Wishlist on Steam <span aria-hidden="true">↗</span></a></section>
    </main>
    <SiteFooter showContact={false} />
  </div>;
}

export function OthercrownPress() {
  return <PressKit
    game={GAME}
    collection={pressFolder}
    cover={{ art: media.pressCover, logo: media.logo, position: 'center 25%', alt: 'Othercrown key art: a hooded figure deals cards behind a slot machine' }}
    icon={media.square.src}
    requestLabel="Request press access by email"
    factGroups={[
      [['Developer:', 'Voidmaw'], ['Genre', 'Auto-battler | Roguelite']],
      [['Platforms & release dates:', <React.Fragment><a href={GAME.steamUrl} target="_blank" rel="noopener noreferrer">Steam</a> - {GAME.releaseWindow} (in development)</React.Fragment>]],
    ]}
    socials={[['Twitter', 'https://x.com/VoidmawGames'], ['Bluesky', 'https://bsky.app/profile/voidmawgames.bsky.social'], ['YouTube', 'https://www.youtube.com/@VoidmawGames/videos']]}
    boxArt={{ ...media.capsule, alt: 'Othercrown portrait capsule' }}
    squareArt={{ ...media.square, alt: 'Othercrown square artwork' }}
    about={[shortDescription, storyIntroduction]}
    features={features}
    closing={closing}
    videos={media.clips}
    artwork={[
      { src: media.keyArt, original: media.keyArtDownload, download: 'Othercrown-key-art.png', label: '4K key art · PNG', alt: 'Othercrown key art: a hooded figure deals cards behind a slot machine' },
      { ...media.banner, download: 'Othercrown-banner.png', label: 'Banner · 3840×1240 PNG', alt: 'Othercrown banner with logo' },
      { ...media.capsule, download: 'Othercrown-capsule.png', label: 'Portrait capsule · PNG', alt: 'Othercrown portrait capsule' },
      { ...media.square, download: 'Othercrown-square.png', label: 'Square artwork · PNG', alt: 'Othercrown square artwork' },
    ]}
    artworkPattern={[1, 1, 2]}
    screenshots={media.screenshots.map((shot, index) => ({ ...shot, download: `Othercrown-screenshot-${index + 1}.png`, label: `Screenshot ${index + 1}` }))}
    gifs={[]}
    logos={[
      { src: media.logo, original: media.logo, download: 'Othercrown-logo.png', label: 'Othercrown logo · PNG' },
      { src: '/Studio/Voidmaw_Black_Logo.png', original: '/Studio/Voidmaw_Black_Logo.png', label: 'Voidmaw logo · black', light: true },
      { src: '/Studio/Voidmaw_White_Logo.png', original: '/Studio/Voidmaw_White_Logo.png', label: 'Voidmaw logo · white' },
    ]}
  >
    <Metadata press />
  </PressKit>;
}
